import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Button, MenuItem,
  FormControl, InputLabel, Select, Tooltip, Alert,
  useMediaQuery, useTheme
} from '@mui/material';
import { HelpOutline as HelpIcon } from '@mui/icons-material';

export default function ExpenseItemDialog({
  open, onClose, onSave,
  editItemMode, itemFormData, setItemFormData,
  itemCategories, moneyLocked = false
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="expense-item-dialog-title"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          onSave();
        }
      }}
    >
      <DialogTitle id="expense-item-dialog-title">{editItemMode ? 'Edit Expense Item' : 'Add Expense Item'}</DialogTitle>
      <DialogContent>
        {moneyLocked && (
          <Alert severity="info" sx={{ mt: 1 }}>
            This expense is marked Paid (reimbursed outside payroll), so Amount and Paid with cannot be changed. Date, Category, Description and Notes can. Untick Paid only if it was marked Paid by mistake: unticking makes it payable again by the next payroll run. If the amount actually reimbursed was wrong, settle the difference with the employee and your accountant instead.
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Date"
              value={itemFormData.item_date}
              onChange={(e) => setItemFormData({ ...itemFormData, item_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel>Category</InputLabel>
              <Select
                value={itemFormData.category}
                label="Category"
                onChange={(e) => setItemFormData({ ...itemFormData, category: e.target.value })}
              >
                {itemCategories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={itemFormData.description}
              onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
              placeholder="e.g., Uber to airport, Hotel 2 nights, Office paper"
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Amount"
              disabled={moneyLocked}
              value={itemFormData.amount}
              onChange={(e) => setItemFormData({ ...itemFormData, amount: e.target.value })}
              InputProps={{ startAdornment: '$' }}
              inputProps={{ step: '0.01', min: '0' }}
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel>Paid with</InputLabel>
              <Select
                value={itemFormData.payment_method || 'personal'}
                label="Paid with"
                disabled={moneyLocked}
                onChange={(e) => setItemFormData({ ...itemFormData, payment_method: e.target.value })}
              >
                <MenuItem value="personal">My own money</MenuItem>
                <MenuItem value="company_card">Company card</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Set this per line — one shopping trip can mix both. Items paid with your own money are added to your reimbursement. Items on the company card are recorded for the receipt trail only; the business already paid for them, so you are not reimbursed again.">
              <HelpIcon fontSize="small" color="action" />
            </Tooltip>
            {itemFormData.payment_method === 'company_card' && (
              <Alert severity="info" sx={{ py: 0, flex: 1 }}>
                Recorded only — not reimbursed
              </Alert>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (optional)"
              value={itemFormData.notes}
              onChange={(e) => setItemFormData({ ...itemFormData, notes: e.target.value })}
              placeholder="Additional details, justification, etc."
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
        <Button onClick={onClose} size={isMobile ? 'large' : 'medium'}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!itemFormData.description || !itemFormData.amount}
          size={isMobile ? 'large' : 'medium'}
        >
          {editItemMode ? 'Update Item' : 'Add Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
